import React, { Dispatch, SetStateAction } from "react";
import { Map, Set } from "immutable";
import { Helpers, useStep } from "../hooks";
import { AllocationBag } from "helpers/allocation-dimension";
import { TargetGroupWithTargets } from "../../types";
import { TargetRowData } from "../left-side/add-target-values/paste-container/paste-container";

export enum RenderedComponent {
    MANAGE_TARGETS_DIALOG = "manage-targets-dialog",
    FINANCIAL_TARGET_FORM_DIALOG = "financial-target-form-dialog",
    EMPTY = "",
}

type TargetWizardContext = {
    steps: { currentStep: 1 | 2; helpers: Helpers };
    renderedComponent: [RenderedComponent, Dispatch<SetStateAction<RenderedComponent>>];
    actions: {
        openNewWizard: (targetBag: AllocationBag) => void;
        openEditWizard: (targetGroupWithTargets: TargetGroupWithTargets, targetBag: AllocationBag) => void;
    };
    targetGroupWithTargetsSnapshot: [
        TargetGroupWithTargets | null,
        Dispatch<SetStateAction<TargetGroupWithTargets | null>>,
    ];
    targetGroupFormIsValid: [boolean, Dispatch<SetStateAction<boolean>>];
    targetAllocationBagBuffer: [AllocationBag, Dispatch<SetStateAction<AllocationBag>>];
    targetAllocationBagBufferIsValid: [boolean, Dispatch<SetStateAction<boolean>>];
    selectedTargetIds: [Set<string>, Dispatch<SetStateAction<Set<string>>>];
    targetRowDataCache: [TargetRowDataCache, Dispatch<SetStateAction<TargetRowDataCache>>];
};

export const TargetWizardContext = React.createContext<TargetWizardContext>(undefined);

export interface TargetRowDataCache {
    [key: string]: TargetRowData[];
}

export const TargetWizardContextProvider = ({ children }) => {
    // Snapshot/original object when editing starts
    const [renderedComponent, setRenderedComponent] = React.useState<RenderedComponent>(null);
    const [targetGroupWithTargetsSnapshot, setTargetGroupWithTargetsSnapshot] =
        React.useState<TargetGroupWithTargets>(null);
    const [targetGroupFormIsValid, setTargetGroupFormIsValid] = React.useState<boolean>(false);
    const [targetAllocationBagBufferIsValid, setTargetAllocationBagBufferIsValid] = React.useState<boolean>(false);
    const [targetAllocationBagBuffer, setTargetAllocationBagBuffer] = React.useState<AllocationBag>(
        Map() as AllocationBag
    );
    const [selectedTargetIds, setSelectedTargetIds] = React.useState<Set<string>>(Set<string>());
    const [targetRowDataCache, setTargetRowDataCache] = React.useState<TargetRowDataCache>({});

    const [currentStep, helpers] = useStep(2);
    const openNewWizard = (targetBag: AllocationBag) => {
        setTargetGroupWithTargetsSnapshot(null);
        setTargetRowDataCache({});
        if (targetBag) {
            setTargetAllocationBagBuffer(targetBag);
        }
        setRenderedComponent(RenderedComponent.FINANCIAL_TARGET_FORM_DIALOG);
        helpers.setStep(1);
    };

    const openEditWizard = (targetGroupWithTargets: TargetGroupWithTargets, targetBag: AllocationBag) => {
        setTargetGroupWithTargetsSnapshot(targetGroupWithTargets);
        setTargetRowDataCache({});
        if (targetBag) {
            setTargetAllocationBagBuffer(targetBag);
        }
        setRenderedComponent(RenderedComponent.FINANCIAL_TARGET_FORM_DIALOG);
        helpers.setStep(1);
    };

    return (
        <TargetWizardContext.Provider
            value={{
                steps: { currentStep: currentStep as 1 | 2, helpers },
                renderedComponent: [renderedComponent, setRenderedComponent],
                actions: { openNewWizard, openEditWizard },
                targetGroupWithTargetsSnapshot: [targetGroupWithTargetsSnapshot, setTargetGroupWithTargetsSnapshot],
                targetGroupFormIsValid: [targetGroupFormIsValid, setTargetGroupFormIsValid],
                targetAllocationBagBuffer: [targetAllocationBagBuffer, setTargetAllocationBagBuffer],
                targetAllocationBagBufferIsValid: [
                    targetAllocationBagBufferIsValid,
                    setTargetAllocationBagBufferIsValid,
                ],
                selectedTargetIds: [selectedTargetIds, setSelectedTargetIds],
                targetRowDataCache: [targetRowDataCache, setTargetRowDataCache],
            }}
        >
            {children}
        </TargetWizardContext.Provider>
    );
};

export const useTargetWizardContext = () => React.useContext(TargetWizardContext);
